import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { Button } from '../components/ui/button'

/*
  useConfirm — vervangt native window.confirm() met een shadcn Dialog.
  Gebruik:
    const confirm = useConfirm()
    if (!(await confirm('Zeker verwijderen?'))) return
    // of:
    if (!(await confirm({ title: 'Verwijderen?', description: 'Kan niet ongedaan gemaakt worden.', confirmLabel: 'Verwijder', destructive: true }))) return

  Wrap de app-root met <ConfirmProvider> (in main.jsx of App.jsx).
*/

const ConfirmContext = createContext(null)

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, title: '', description: '', confirmLabel: 'Bevestig', cancelLabel: 'Annuleer', destructive: false })
  const resolverRef = useRef(null)

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      const o = typeof opts === 'string' ? { title: opts } : (opts || {})
      resolverRef.current = resolve
      setState({
        open: true,
        title: o.title || 'Bevestigen?',
        description: o.description || '',
        confirmLabel: o.confirmLabel || 'Bevestig',
        cancelLabel: o.cancelLabel || 'Annuleer',
        destructive: !!o.destructive,
      })
    })
  }, [])

  const handleAnswer = (answer) => {
    setState(s => ({ ...s, open: false }))
    const r = resolverRef.current
    resolverRef.current = null
    if (r) r(answer)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => { if (!o) handleAnswer(false) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{state.title}</DialogTitle>
            {state.description && <DialogDescription>{state.description}</DialogDescription>}
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => handleAnswer(false)}>{state.cancelLabel}</Button>
            <Button variant={state.destructive ? 'destructive' : 'default'} onClick={() => handleAnswer(true)}>{state.confirmLabel}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    // Fallback voor components die outside provider draaien
    return async (opts) => window.confirm(typeof opts === 'string' ? opts : (opts?.title || 'Bevestigen?'))
  }
  return ctx
}
